import { BTAX_ACCOUNT_CART_ITEM_TYPE } from 'egov-api';
import { Injectable } from '@angular/core';
import { sync } from '@ces/sync';
import { Store } from '@ngrx/store';
import { State } from '@state/model';
import { of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CartActionsFacade } from '../../cart/services/cart-actions.facade';
import { selectBTaxAccount, selectBTaxAccountIsInCart, selectBTaxAccountIsPayable } from '../state/entities/account/btax-account.selectors';

@Injectable({ providedIn: 'any' })
export class BTaxCart
{

    constructor
    (
        // Dependencies

        private readonly store: Store<State>,
        private readonly cartActions: CartActionsFacade,
    )
    {}


    // Adding and removing bills

    public getAddToOrRemoveFromCartTitle$(accountNumber: string)
    {
        return this.store.select(selectBTaxAccount(accountNumber)).pipe(
            switchMap(account => account
                ? this.store.select(selectBTaxAccountIsInCart(accountNumber)).pipe(
                    map(inCart =>
                    {
                        if (inCart) return 'Remove this bill from the cart';
                        if (!account.due?.total) return 'This bill cannot be added to the cart because it doesn\'t have a balance due';
                        return 'Add this bill to the cart';

                    })
                )
                : of(undefined)
            )
        );
    }


    // Adding / Removing entire accounts

    public toggleAccountInCart(
        accountNumber: string,
        notifyErrors: boolean = false,
        retry: () => any = () => this.toggleAccountInCart(accountNumber, notifyErrors)
    ): Subject<void>
    {
        const inCartAlready = sync(this.store.select(selectBTaxAccountIsInCart(accountNumber)));

        if (!inCartAlready)
            return this.cartActions.addItemsToCart(
                [{ type: BTAX_ACCOUNT_CART_ITEM_TYPE, id: accountNumber }],
                true,
                notifyErrors,
                retry
            );
        else
            return this.cartActions.removeItemsFromCart(
                [{ type: BTAX_ACCOUNT_CART_ITEM_TYPE, id: accountNumber }],
                true,
                notifyErrors,
                retry
            );
    }

    public getAddAccountToOrRemoveFromCartTitle$(accountNumber: string)
    {
        return this.store.select(selectBTaxAccountIsPayable(accountNumber)).pipe(
            switchMap(isPayable => isPayable === undefined
                ? of(undefined)
                : isPayable
                    ? this.store.select(selectBTaxAccountIsInCart(accountNumber)).pipe(
                        map(inCart => !inCart
                            ? 'Add bills for this parcel to the cart.'
                            : 'Remove bills for this parcel from the cart.'
                        )
                    )
                    : of('This parcel cannot be added to the cart because it doesn\'t have unpaid bills with an available balance due.')
            )
        );
    }
}
